import { fetchBaseQuery } from "@reduxjs/toolkit/query";
import type {
  BaseQueryFn,
  FetchBaseQueryError,
  FetchArgs,
} from "@reduxjs/toolkit/query";
import type { RootState } from "../redux/store";
import { logOut, setCredentials, type authData } from "../redux/authSlice";



export const rtkBaseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_DOMAIN,
  credentials: "include",
  prepareHeaders: (headers, { getState, endpoint }) => {
    const token = (getState() as RootState).auth.token;
    const excludedEndpoints = ["login", "register"];
    if (token && !excludedEndpoints.includes(endpoint.toLowerCase())) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

let refreshPromise: Promise<authData | null> | null = null;
export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  any,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await rtkBaseQuery(args, api, extraOptions);

  if (result?.error?.status === 401 && result.error) {
    const state = api.getState() as RootState;

    if (!refreshPromise) {
      const { refreshToken, userName } = state.auth;

      // We wrap the refresh call in a promise manually
      refreshPromise = (async () => {
        const refreshResult = await rtkBaseQuery(
          {
            url: "users/tokenrefresh",
            method: "POST",
            body: JSON.stringify({ refreshToken, userName }),
            headers: { "Content-Type": "application/json" },
          },
          api,
          extraOptions
        ) as { data?: { data?: authData } };
        if (refreshResult?.data?.data) {
          const newAuth: authData = {
            ...state.auth,
            token: refreshResult.data.data.token,
            refreshToken: refreshResult.data.data.refreshToken,
            expiration: refreshResult.data.data.expiration,
            refreshTokenExpiration: refreshResult.data.data.refreshTokenExpiration,
          };
          api.dispatch(setCredentials(newAuth));
          return newAuth;
        } else {
          api.dispatch(logOut());
          return null;
        }
      })();
      refreshPromise.finally(() => {
        refreshPromise = null;
      });
    }
    const refreshedUser = await refreshPromise;
    if (refreshedUser) {
      result = await rtkBaseQuery(args, api, extraOptions);
    }
  }
  return result;
};
